"use client"

import type React from "react"

import { useEffect, useState, type FormEvent, useRef } from "react"
import { ArrowLeft, Eye, FileText, ImageIcon, Paperclip, Send, Users, XCircle } from "lucide-react"
import { Form } from "react-bootstrap"
import { Link, useParams, useNavigate } from "react-router-dom"
import { useTranslation } from "../../hooks/use-translation"
import { useAppDispatch, useAppSelector } from "../../redux/hooks"
import { markThreadAsRead, sendMessage, fetchThreadById } from "../../redux/slices/messagesSlice"
import { formatDate } from "../../utils/timestamp"
import { useAuth } from "../../contexts/auth-context"
import AttachmentPreviewModal from "../../components/AttachmentPreviewModal"

export default function ThreadPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { user } = useAuth()
  const { threads, observedThreads = [] } = useAppSelector((state) => state.messages)
  const params = useParams()
  const threadId = params.threadId as string
  const [thread, setThread] = useState(
    threads.find((t) => t.id === threadId) || observedThreads.find((t) => t.id === threadId) || null,
  )
  const isObserver = thread?.isObserver ?? observedThreads.some((t) => t.id === threadId)
  const [newMessage, setNewMessage] = useState("")
  const [attachment, setAttachment] = useState<File | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const [showAttachmentModal, setShowAttachmentModal] = useState(false)
  const [selectedAttachment, setSelectedAttachment] = useState<{
    url: string
    name: string
    type: string
  } | null>(null)

  useEffect(() => {
    const currentThread =
      threads.find((t) => t.id === threadId) || observedThreads.find((t) => t.id === threadId)
    if (currentThread) {
      setThread(currentThread)

      if (!currentThread.messages || currentThread.messages.length === 0) {
        dispatch(fetchThreadById(threadId))
      }

      if (currentThread.unread && user && !currentThread.isObserver) {
        dispatch(markThreadAsRead(threadId))
      }
    } else {
      dispatch(fetchThreadById(threadId))
    }
  }, [threadId, threads, observedThreads, dispatch, user])

  useEffect(() => {
    scrollToBottom()
  }, [thread])

  const scrollToBottom = () => {
    setTimeout(() => {
      const isMobile = window.innerWidth < 768

      if (isMobile) {
        window.scrollTo({
          top: document.body.scrollHeight,
          behavior: "smooth",
        })
      } else {
        const desktopContainer = document.querySelector(".d-none.d-md-flex .overflow-auto")
        if (desktopContainer) {
          desktopContainer.scrollTop = desktopContainer.scrollHeight
          return
        }
      }

      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({
          behavior: "smooth",
          block: "end",
        })
      }
    }, 200)
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setAttachment(e.target.files[0])
    }
  }

  const clearAttachment = () => {
    setAttachment(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }

  const getFileIcon = (fileType: string) => {
    if (fileType.startsWith("image/")) {
      return <ImageIcon className="size-4" />
    } else if (fileType.includes("pdf")) {
      return <FileText className="size-4" />
    } else {
      return <Paperclip className="size-4" />
    }
  }

  const handleSendMessage = (e: FormEvent) => {
    e.preventDefault()
    if ((!newMessage.trim() && !attachment) || !user || !thread) return

    dispatch(
      sendMessage({
        threadId: thread.id,
        content: newMessage,
        attachment: attachment,
      }),
    )
    setNewMessage("")
    setAttachment(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }

  const handleAttachmentClick = (url: string, name: string, type: string) => {
    setSelectedAttachment({ url, name, type })
    setShowAttachmentModal(true)
  }

  if (!thread) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "calc(100vh - 8rem)" }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    )
  }

  return (
    <>
      {/* Mobile Layout */}
      <div className="d-md-none" style={{ display: "flex", flexDirection: "column", minHeight: "100dvh" }}>
        {/* Fixed Header - Mobile */}
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1030,
            height: "70px",
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          }}
        >
          <div className="d-flex align-items-center gap-3 h-100 px-3">
            <Link to="/dashboard/messages">
              <button className="btn-back-circle">
                <ArrowLeft size={20} />
                <span className="visually-hidden">{t("back")}</span>
              </button>
            </Link>
            <div className="d-flex align-items-center gap-2">
              {thread.type === "building-wide" ? (
                <div
                  className="d-flex align-items-center justify-content-center bg-white text-primary rounded-circle"
                  style={{ width: 40, height: 40 }}
                >
                  <Users size={20} />
                </div>
              ) : (
                <img
                  src={thread.participantPhoto || "/placeholder.svg"}
                  alt={thread.participantName}
                  width={40}
                  height={40}
                  className="rounded-circle"
                />
              )}
              <div>
                <h1 className="fs-4 fw-bold mb-0 text-white">{thread.participantName}</h1>
                {thread.type === "building-wide" && thread.participantCount && (
                  <small className="text-white opacity-75">
                    {thread.participantCount} {t("members")}
                  </small>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Messages Area - Mobile */}
        <div
          className="flex-grow-1 overflow-auto p-3"
          style={{
            paddingTop: "20px",
            marginBottom: attachment ? "370px" : "230px",
          }}
        >
          <div className="d-flex flex-column gap-3">
            {(thread.messages || []).map((message) => {
              const isCurrentUser = message.senderId === user?.id
              return (
                <div key={message.id} className={`d-flex ${isCurrentUser ? "justify-content-end" : ""}`}>
                  <div className="d-flex gap-2" style={{ maxWidth: "80%" }}>
                    {!isCurrentUser && (
                      <img
                        src={message.senderPhoto || "/placeholder.svg"}
                        alt={message.senderName}
                        width={40}
                        height={40}
                        className="rounded-circle"
                      />
                    )}
                    <div
                      className="rounded-4 p-3"
                      style={{
                        background: isCurrentUser ? "rgba(255, 255, 255, 0.9)" : "rgba(255, 255, 255, 0.1)",
                        backdropFilter: "blur(10px)",
                        border: "1px solid rgba(255, 255, 255, 0.2)",
                        color: isCurrentUser ? "#333" : "white",
                      }}
                    >
                      <div className="mb-1">
                        <span className="small fw-medium d-block">{message.senderName}</span>
                        <span className="small opacity-75 d-block">{formatDate(message.timestamp)}</span>
                      </div>
                      {message.content && (
                        <p className="mb-0 small" style={{ whiteSpace: "pre-wrap" }}>
                          {message.content}
                        </p>
                      )}
                      {message.hasAttachment && (
                        <div className="mt-2">
                          <div
                            onClick={() =>
                              handleAttachmentClick(
                                message.attachmentUrl || "",
                                message.attachmentName || "Attachment",
                                message.attachmentType || "",
                              )
                            }
                            className={`d-flex align-items-center gap-2 p-2 rounded cursor-pointer ${isCurrentUser ? "bg-white text-primary" : "bg-primary-subtle"}`}
                            style={{ cursor: "pointer" }}
                          >
                            {getFileIcon(message.attachmentType || "")}
                            <span className="small text-truncate">{message.attachmentName || "Attachment"}</span>
                          </div>
                        </div>
                      )}
                    </div>
                    {isCurrentUser && (
                      <img
                        src={message.senderPhoto || "/placeholder.svg"}
                        alt={message.senderName}
                        width={40}
                        height={40}
                        className="rounded-circle"
                      />
                    )}
                  </div>
                </div>
              )
            })}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Fixed Input - Mobile */}
        {isObserver ? (
          <div
            style={{
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 1030,
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              borderTop: "1px solid rgba(255, 255, 255, 0.2)",
            }}
            className="py-3 px-3"
          >
            <div className="d-flex align-items-center gap-2 text-white opacity-75">
              <Eye size={16} />
              <span className="small">{t("observerModeMessage")}</span>
            </div>
          </div>
        ) : (
        <div
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1030,
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            borderTop: "1px solid rgba(255, 255, 255, 0.2)",
          }}
          className="py-3"
        >
          <Form onSubmit={handleSendMessage} className="px-3">
            <Form.Group className="mb-2">
              <Form.Control
                as="textarea"
                rows={4}
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type your message"
                className="bg-white border-0 rounded-3"
                size="lg"
              />
            </Form.Group>

            {attachment && (
              <div
                className="mb-3 p-2 rounded-3 d-flex justify-content-between align-items-center"
                style={{
                  background: "rgba(255, 255, 255, 0.1)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                }}
              >
                <div className="d-flex align-items-center gap-2 text-white">
                  {getFileIcon(attachment.type)}
                  <span className="small text-truncate">{attachment.name}</span>
                </div>
                <button variant="link" className="p-0 text-white" onClick={clearAttachment}>
                  <XCircle className="size-4" />
                </button>
              </div>
            )}

            <div className="d-flex justify-content-between align-items-center">
              <button variant="" className="btn-cancel-outline" onClick={() => fileInputRef.current?.click()}>
                <Paperclip className="size-4 me-1" />
                Attach
              </button>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                style={{ display: "none" }}
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
              />

              <button type="submit" className="btn-white-pill-bold" disabled={!newMessage.trim() && !attachment}>
                <Send className="size-4 me-2" />
                {t("sendMessage")}
              </button>
            </div>
          </Form>
        </div>
        )}
      </div>

      {/* Desktop Layout */}
      <div className="d-none d-md-flex flex-column py-3" style={{ height: "calc(100vh - 8rem)" }}>
        <div className="mb-3 d-flex align-items-center gap-3">
          <Link to="/dashboard/messages">
            <button className="btn-back-circle">
              <ArrowLeft size={20} />
              <span className="visually-hidden">{t("back")}</span>
            </button>
          </Link>
          <div className="d-flex align-items-center gap-2">
            {thread.type === "building-wide" ? (
              <div
                className="d-flex align-items-center justify-content-center bg-white text-primary rounded-circle"
                style={{ width: 40, height: 40 }}
              >
                <Users size={20} />
              </div>
            ) : (
              <img
                src={thread.participantPhoto || "/placeholder.svg"}
                alt={thread.participantName}
                width={40}
                height={40}
                className="rounded-circle"
              />
            )}
            <div>
              <h1 className="fs-4 fw-bold mb-0 text-white">{thread.participantName}</h1>
              {thread.type === "building-wide" && thread.participantCount && (
                <small className="text-white opacity-75">
                  {thread.participantCount} {t("members")}
                </small>
              )}
            </div>
          </div>
        </div>

        <div
          className="rounded-4 flex-grow-1 d-flex flex-column"
          style={{
            minHeight: 0,
            background: "rgba(255, 255, 255, 0.1)",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(255, 255, 255, 0.2)",
          }}
        >
          <div className="flex-grow-1 overflow-auto p-3" style={{ minHeight: 0 }}>
            <div className="d-flex flex-column gap-3">
              {(thread.messages || []).map((message) => {
                const isCurrentUser = message.senderId === user?.id
                return (
                  <div key={message.id} className={`d-flex ${isCurrentUser ? "justify-content-end" : ""}`}>
                    <div className="d-flex gap-2" style={{ maxWidth: "80%" }}>
                      {!isCurrentUser && (
                        <img
                          src={message.senderPhoto || "/placeholder.svg"}
                          alt={message.senderName}
                          width={40}
                          height={40}
                          className="rounded-circle"
                        />
                      )}
                      <div
                        className="rounded-4 p-3"
                        style={{
                          background: isCurrentUser ? "rgba(255, 255, 255, 0.9)" : "rgba(255, 255, 255, 0.1)",
                          backdropFilter: "blur(10px)",
                          border: "1px solid rgba(255, 255, 255, 0.2)",
                          color: isCurrentUser ? "#333" : "white",
                        }}
                      >
                        <div className="mb-1">
                          <span className="small fw-medium d-block">{message.senderName}</span>
                          <span className="small opacity-75 d-block">{formatDate(message.timestamp)}</span>
                        </div>
                        {message.content && (
                          <p className="mb-0 small" style={{ whiteSpace: "pre-wrap" }}>
                            {message.content}
                          </p>
                        )}
                        {message.hasAttachment && (
                          <div className="mt-2">
                            <div
                              onClick={() =>
                                handleAttachmentClick(
                                  message.attachmentUrl || "",
                                  message.attachmentName || "Attachment",
                                  message.attachmentType || "",
                                )
                              }
                              className={`d-flex align-items-center gap-2 p-2 rounded cursor-pointer ${isCurrentUser ? "bg-white text-primary" : "bg-primary-subtle"}`}
                              style={{ cursor: "pointer" }}
                            >
                              {getFileIcon(message.attachmentType || "")}
                              <span className="small text-truncate">{message.attachmentName || "Attachment"}</span>
                            </div>
                          </div>
                        )}
                      </div>
                      {isCurrentUser && (
                        <img
                          src={message.senderPhoto || "/placeholder.svg"}
                          alt={message.senderName}
                          width={40}
                          height={40}
                          className="rounded-circle"
                        />
                      )}
                    </div>
                  </div>
                )
              })}
              <div ref={messagesEndRef} />
            </div>
          </div>
          {isObserver ? (
          <div className="border-top border-white border-opacity-25 p-4">
            <div className="d-flex align-items-center gap-2 text-white opacity-75">
              <Eye size={18} />
              <span>{t("observerModeMessage")}</span>
            </div>
          </div>
          ) : (
          <div className="border-top border-white border-opacity-25 p-4">
            <Form onSubmit={handleSendMessage}>
              <Form.Group className="mb-3">
                <Form.Label className="text-white">Add Comment</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={6}
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type your message"
                  className="bg-white border-0 rounded-3"
                />
              </Form.Group>

              {attachment && (
                <div
                  className="mb-3 p-3 rounded-3 d-flex justify-content-between align-items-center"
                  style={{
                    background: "rgba(255, 255, 255, 0.1)",
                    backdropFilter: "blur(10px)",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                  }}
                >
                  <div className="d-flex align-items-center gap-2 text-white">
                    {getFileIcon(attachment.type)}
                    <span className="text-truncate">{attachment.name}</span>
                  </div>
                  <button variant="link" className="p-0 text-white" onClick={clearAttachment}>
                    <XCircle className="size-4" />
                  </button>
                </div>
              )}

              <div className="d-flex justify-content-between align-items-center">
                <button variant="" className="btn-cancel-outline" onClick={() => fileInputRef.current?.click()}>
                  <Paperclip className="size-4 me-1" />
                  {t("attachFile")}
                </button>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                />

                <button type="submit" className="btn-white-pill-bold" disabled={!newMessage.trim() && !attachment}>
                  <Send className="size-4 me-2" />
                  {t("sendMessage")}
                </button>
              </div>
            </Form>
          </div>
          )}
        </div>
      </div>

      {/* Attachment Preview Modal */}
      {selectedAttachment && (
        <AttachmentPreviewModal
          show={showAttachmentModal}
          onHide={() => setShowAttachmentModal(false)}
          attachmentUrl={selectedAttachment.url}
          attachmentName={selectedAttachment.name}
          attachmentType={selectedAttachment.type}
        />
      )}
    </>
  )
}
